/**
 * Meta (Facebook) Pixel — production only.
 *
 * Loads Meta's pixel and fires the base `PageView`, but ONLY when BOTH gates
 * hold (belt-and-suspenders, so staging can never light it up):
 *   1. `VITE_META_PIXEL_ID` is set at build time (mirrors the PostHog
 *      `VITE_POSTHOG_KEY` no-op-when-absent pattern in `analytics.ts`).
 *   2. the page is being served from the production host — never staging,
 *      a Vercel preview, or localhost.
 *
 * If either gate fails every export is a safe no-op, so this is always safe to
 * call. The pixel lives in the portal shell only; games run in sandboxed
 * iframes (separate documents) so it never enters a game — preserving the
 * `/privacy` "no third-party trackers in any game iframe" promise.
 *
 * Note: the standard `<noscript>` fallback image is intentionally omitted — it
 * can't be host/env-gated (it would fire on staging too) and the SPA requires
 * JS to render at all, so a no-JS pixel adds nothing.
 *
 * The pixel id is public (not a secret); it is set in the Vercel **Production**
 * environment as `VITE_META_PIXEL_ID` and left unset everywhere else.
 */

/** fbq is a callable with a few properties Meta's loader stub attaches. */
interface Fbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: (...args: unknown[]) => void;
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/** Exact-match production hosts. Exact match (not `endsWith`) so
 * `staging.sportsacademygames.com` and `*.vercel.app` previews are excluded. */
const PROD_HOSTS = new Set(["sportsacademygames.com", "www.sportsacademygames.com"]);

/** True only on the production portal domain. Pure + exported so the
 * "never fires on staging" guarantee is unit-testable. */
export function isProductionHost(hostname: string): boolean {
  return PROD_HOSTS.has(hostname.toLowerCase());
}

let enabled = false;

/** Meta's loader stub, expressed in TS: install the queueing `fbq` shim and
 * inject `fbevents.js`. Mirrors the official base-code IIFE. */
function bootstrapPixel(): Fbq {
  if (window.fbq) return window.fbq;
  const fbq = function (...args: unknown[]): void {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as unknown as Fbq;
  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(script, first);
  return fbq;
}

/**
 * Boot the Meta Pixel + fire the initial PageView. No-op unless the build
 * carries `VITE_META_PIXEL_ID` AND we're on the production host. Idempotent.
 */
export function initMetaPixel(): void {
  if (typeof window === "undefined") return; // SSR / prerender
  if (enabled) return;
  const id = import.meta.env.VITE_META_PIXEL_ID;
  if (!id) return;
  if (!isProductionHost(window.location.hostname)) return;

  const fbq = bootstrapPixel();
  fbq("init", id);
  fbq("track", "PageView");
  enabled = true;
}

/**
 * The exact `fbq(...)` argument list for one event — pure, so the shape Meta
 * receives is unit-testable without booting the pixel. `eventId`, when
 * provided, rides as Meta's `eventID` so the browser event deduplicates
 * against the same event sent server-side via the Conversions API.
 */
export function fbqArgs(
  method: "track" | "trackCustom",
  event: string,
  params?: Record<string, unknown>,
  eventId?: string,
): unknown[] {
  if (eventId) return [method, event, params ?? {}, { eventID: eventId }];
  if (params) return [method, event, params];
  return [method, event];
}

/**
 * Fire a Meta Pixel STANDARD event (`Lead`, `Purchase`, … — names Meta knows
 * natively). No-op when the pixel isn't enabled.
 */
export function trackMetaEvent(
  event: string,
  params?: Record<string, unknown>,
  eventId?: string,
): void {
  if (!enabled || typeof window === "undefined" || !window.fbq) return;
  window.fbq(...fbqArgs("track", event, params, eventId));
}

/**
 * Fire a Meta Pixel CUSTOM event (`fbq('trackCustom', …)`) — a name Meta does
 * not know natively, which the ad account turns into a custom conversion or an
 * audience in Events Manager (the funding campaign optimises on
 * `funding_consent_checked` and excludes on `funding_phone_submitted`). Same
 * gates and dedup semantics as {@link trackMetaEvent}.
 */
export function trackMetaCustomEvent(
  event: string,
  params?: Record<string, unknown>,
  eventId?: string,
): void {
  if (!enabled || typeof window === "undefined" || !window.fbq) return;
  window.fbq(...fbqArgs("trackCustom", event, params, eventId));
}

/** Random id shared by a browser pixel event and its server-side (Conversions
 * API) mirror so Meta counts the pair once. Pure of pixel state — callers may
 * mint one even when the pixel is disabled (the server event still wants it). */
export function newMetaEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Read one cookie value by name, or null. Exported for tests. */
export function readCookie(name: string, cookieString = document.cookie): string | null {
  for (const part of cookieString.split(";")) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() === name) {
      const v = part.slice(eq + 1).trim();
      return v.length > 0 ? v : null;
    }
  }
  return null;
}

/**
 * Meta's browser identifiers for Conversions API matching: `_fbp` (set by the
 * pixel for every visitor) and `_fbc` (set when the visit carried an fbclid).
 * Either may be absent — ad-blockers, first hit before the pixel loads, or
 * non-prod hosts where the pixel never boots. Safe anywhere: just cookie reads.
 */
export function getMetaBrowserIds(): { fbp?: string; fbc?: string } {
  if (typeof document === "undefined") return {};
  const out: { fbp?: string; fbc?: string } = {};
  const fbp = readCookie("_fbp");
  const fbc = readCookie("_fbc");
  if (fbp) out.fbp = fbp;
  if (fbc) out.fbc = fbc;
  return out;
}
